/**
 * SuspendGuard - detects the large wall-clock gap that appears in the first
 * animation frame after a device suspend/resume (laptop sleep, lid close, OS
 * suspend).
 *
 * MUS-175: on suspend the audio clock (Tone.context.currentTime, read through
 * TimeKeeper.audioTimeToMeasureTimestamp) is free-running, so on wake a single
 * frame observes an elapsed interval equal to the whole away-duration. Left
 * unguarded that jump pushes the measure timestamp past the phrase total
 * duration and the exercise falsely completes with no user input.
 *
 * During a genuine suspend `performance.now()` freezes alongside
 * requestAnimationFrame, so the first frame after wake reports an elapsed time
 * equal to the away-duration - orders of magnitude larger than any normal, or
 * even heavily throttled, foreground frame. That single wall-clock measurement
 * is a reliable suspend/resume signal and holds regardless of which suspend
 * path fired (visibilitychange, Capacitor appStateChange, or neither).
 *
 * The guard is deliberately clock-injectable so it is fully unit-testable
 * without a real timer.
 */

/**
 * Threshold above which a single inter-frame interval is treated as a
 * suspend/resume rather than real elapsed play time. A normal foreground frame
 * is ~16ms and even a badly stalled main thread stays well under a second;
 * while the Phaser loop is parked (sleepGame on visibilitychange) no frames are
 * sampled at all, so background rAF throttling never reaches this path. One
 * second cleanly separates a normal frame from a real device sleep, with wide
 * margin on both sides. Single tuning point.
 */
export const DEFAULT_SUSPEND_GAP_MS = 1000;

export class SuspendGuard {
  private lastSampleMs: number | null = null;

  constructor(
    private readonly thresholdMs: number = DEFAULT_SUSPEND_GAP_MS,
    private readonly now: () => number = () =>
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now(),
  ) {}

  /**
   * Record the current time and report whether the interval since the previous
   * sample exceeds the suspend threshold. The first call after construction or
   * reset() only establishes a baseline and always returns false.
   */
  sample(): boolean {
    const now = this.now();
    const previous = this.lastSampleMs;
    this.lastSampleMs = now;
    if (previous === null) return false;
    return now - previous > this.thresholdMs;
  }

  /**
   * Drop the baseline so the next sample() re-establishes it. Call whenever
   * playback (re)starts or resumes, so the away-gap is consumed by the pause
   * decision and never leaks into the fresh, re-anchored clock.
   */
  reset(): void {
    this.lastSampleMs = null;
  }
}
